import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { resolveEmailContext } from '@/lib/emailContext';
import { executeEmailAction } from './emailCommands';

/**
 * Thin wrappers over the action engine (stores/emailCommands.ts), keeping the
 * exported names existing call sites use. All semantics — context resolution,
 * thread expansion, optimistic apply, undo, failure handling — live in the
 * engine; nothing here may add behavior.
 *
 * The accountId/folderId parameters are retained for signature compatibility
 * but intentionally IGNORED: the engine resolves every id's real context via
 * resolveEmailContext (caller-supplied context was a recurring source of
 * unified-id leaks and wrong-account dispatches).
 */
interface EmailActionsStoreState {
  starEmail: (emailId: string, accountId: string, folderId: string) => Promise<void>;
  archiveEmail: (emailId: string, accountId: string, folderId: string) => Promise<void>;
  deleteEmail: (emailId: string, accountId: string, folderId: string) => Promise<void>;
  markRead: (
    emailId: string,
    accountId: string,
    folderId: string,
    read: boolean
  ) => Promise<void>;
}

export const useEmailActionsStore = create<EmailActionsStoreState>()(
  conditionalDevtools(
    () => ({
      starEmail: async (emailId, _accountId, folderId) => {
        // Toggle based on the member's ACTUAL flag from the index — the
        // projected list row aggregates starred with OR across thread members,
        // so reading the row would send the wrong target state (A16).
        const context = resolveEmailContext(emailId, folderId || null);
        const nextStarred = context?.email ? !context.email.starred : true;
        await executeEmailAction(nextStarred ? 'star' : 'unstar', {
          kind: 'messages',
          ids: [emailId],
        });
      },

      archiveEmail: async (emailId, _accountId, folderId) => {
        await executeEmailAction(
          'archive',
          { kind: 'threadRows', ids: [emailId] },
          { contextFolderId: folderId || null }
        );
      },

      deleteEmail: async (emailId, _accountId, folderId) => {
        await executeEmailAction(
          'delete',
          { kind: 'threadRows', ids: [emailId] },
          { contextFolderId: folderId || null }
        );
      },

      markRead: async (emailId, _accountId, folderId, read) => {
        await executeEmailAction(
          read ? 'markRead' : 'markUnread',
          { kind: 'threadRows', ids: [emailId] },
          { contextFolderId: folderId || null }
        );
      },
    }),
    { name: 'EmailActionsStore' }
  )
);
